import { useEffect, useRef } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import { getAssetLoadError } from "./getAssetLoadError";

interface QueryState<Data> extends Omit<
  Parameters<typeof getAssetLoadError>[0],
  "data"
> {
  data: Data | undefined;
  isPlaceholderData?: boolean;
}

export function useAssetListQueryState<Data>(query: QueryState<Data>) {
  const accountId = useCurrentAccountId();
  const previous = useRef<{ accountId: typeof accountId; data: Data }>();
  const { data, isError, isPlaceholderData } = query;

  useEffect(() => {
    if (!isError && !isPlaceholderData && data !== undefined) {
      previous.current = { accountId, data };
    }
  }, [accountId, data, isError, isPlaceholderData]);

  const previousResponse = previous.current;
  const canReusePreviousData =
    isError &&
    accountId !== undefined &&
    previousResponse?.accountId === accountId;
  const hasCurrentData = data !== undefined && data !== null;

  let retainedData = data;
  if (!hasCurrentData && canReusePreviousData) {
    retainedData = previousResponse.data;
  }

  return {
    data: retainedData,
    loadError: getAssetLoadError({ ...query, data: retainedData }),
  };
}
